import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  Select,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Alert,
  AlertIcon,
  Tooltip,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ViewIcon, EditIcon } from "@chakra-ui/icons";
import { FiRefreshCw } from "react-icons/fi";
import { programService, portfolioService } from "../services/edosService";

const STATUS_COLORS = {
  active: "green",
  planning: "blue",
  on_hold: "orange",
  completed: "teal",
  cancelled: "red",
};

export default function ProgramsPage() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onClose: onDetailClose,
  } = useDisclosure();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [programs, setPrograms] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterPortfolioId, setFilterPortfolioId] = useState("");
  const [form, setForm] = useState({
    programName: "",
    portfolioId: "",
    description: "",
    status: "planning",
  });

  const fetchPortfolios = useCallback(async () => {
    try {
      const res = await portfolioService.list();
      setPortfolios(res.data.data || res.data.portfolios || []);
    } catch (_) {}
  }, []);

  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPortfolioId) params.portfolioId = filterPortfolioId;
      const res = await programService.list(params);
      setPrograms(res.data.data || res.data.programs || []);
    } catch (err) {
      toast({ title: "Failed to load programs", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [filterPortfolioId, toast]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);
  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const handleOpenCreate = () => {
    setEditMode(false);
    setEditId(null);
    setForm({
      programName: "",
      portfolioId: filterPortfolioId,
      description: "",
      status: "planning",
    });
    onOpen();
  };

  const handleOpenEdit = (p) => {
    setEditMode(true);
    setEditId(p._id);
    setForm({
      programName: p.programName,
      portfolioId: p.portfolioId?._id || p.portfolioId || "",
      description: p.description || "",
      status: p.status || "planning",
    });
    onOpen();
  };

  const handleSubmit = async () => {
    if (!form.programName) {
      toast({ title: "Program name is required", status: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.portfolioId) delete payload.portfolioId;
      if (editMode) {
        await programService.update(editId, payload);
        toast({ title: "Program updated", status: "success" });
      } else {
        await programService.create(payload);
        toast({ title: "Program created", status: "success" });
      }
      onClose();
      fetchPrograms();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleView = async (p) => {
    setSelectedProgram(null);
    onDetailOpen();
    setDetailLoading(true);
    try {
      const res = await programService.getById(p._id);
      setSelectedProgram(res.data.data || res.data.program || res.data);
    } catch (err) {
      toast({ title: "Failed to load program details", status: "error" });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    try {
      await programService.delete(id);
      toast({ title: "Program deleted", status: "success" });
      fetchPrograms();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    }
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Programs
          </Text>
          <Text color="gray.500" fontSize="sm">
            Manage strategic programs across portfolios
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={fetchPrograms}
          />
          <Button
            leftIcon={<AddIcon />}
            colorScheme="teal"
            onClick={handleOpenCreate}
          >
            New Program
          </Button>
        </HStack>
      </Flex>

      <Flex gap={3} mb={4} align="center">
        <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
          Filter by Portfolio:
        </Text>
        <Select
          placeholder="All portfolios"
          value={filterPortfolioId}
          onChange={(e) => setFilterPortfolioId(e.target.value)}
          maxW="320px"
        >
          {portfolios.map((p) => (
            <option key={p._id} value={p._id}>
              {p.portfolioName}
            </option>
          ))}
        </Select>
      </Flex>

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        overflow="hidden"
      >
        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" color="teal.500" />
          </Flex>
        ) : programs.length === 0 ? (
          <Alert status="info" m={4}>
            <AlertIcon />
            No programs found.
          </Alert>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg={headerBg}>
              <Tr>
                <Th>Program</Th>
                <Th>Portfolio</Th>
                <Th>Status</Th>
                <Th>Projects</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {programs.map((p) => (
                <Tr key={p._id}>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="semibold" fontSize="sm">
                        {p.programName}
                      </Text>
                      {p.description && (
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                          {p.description}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {p.portfolioId?.portfolioName || "-"}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={STATUS_COLORS[p.status] || "gray"}>
                      {p.status?.replace(/_/g, " ") || "-"}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme="teal">
                      {p.projects?.length || p.projectCount || 0}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {p.createdAt
                        ? new Date(p.createdAt).toLocaleDateString()
                        : "-"}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Tooltip label="View Details">
                        <IconButton
                          size="xs"
                          icon={<ViewIcon />}
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleView(p)}
                        />
                      </Tooltip>
                      <Tooltip label="Edit">
                        <IconButton
                          size="xs"
                          icon={<EditIcon />}
                          variant="ghost"
                          colorScheme="orange"
                          onClick={() => handleOpenEdit(p)}
                        />
                      </Tooltip>
                      <Tooltip label="Delete">
                        <IconButton
                          size="xs"
                          icon={<DeleteIcon />}
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(p._id)}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Create / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editMode ? "Edit Program" : "Create Program"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Program Name</FormLabel>
                <Input
                  value={form.programName}
                  onChange={(e) =>
                    setForm({ ...form, programName: e.target.value })
                  }
                  placeholder="e.g. Customer 360"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Portfolio</FormLabel>
                <Select
                  value={form.portfolioId}
                  onChange={(e) =>
                    setForm({ ...form, portfolioId: e.target.value })
                  }
                >
                  <option value="">No portfolio</option>
                  {portfolios.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.portfolioName}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="planning">Planning</option>
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Optional description"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="teal"
              isLoading={submitting}
              onClick={handleSubmit}
            >
              {editMode ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedProgram?.programName || "Program Detail"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {detailLoading ? (
              <Flex justify="center" p={6}>
                <Spinner />
              </Flex>
            ) : selectedProgram ? (
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={3} spacing={3}>
                  <Box p={3} bg={headerBg} borderRadius="md">
                    <Stat>
                      <StatLabel>Status</StatLabel>
                      <StatNumber fontSize="md">
                        {selectedProgram.status}
                      </StatNumber>
                    </Stat>
                  </Box>
                  <Box p={3} bg={headerBg} borderRadius="md">
                    <Stat>
                      <StatLabel>Projects</StatLabel>
                      <StatNumber>
                        {selectedProgram.projects?.length || 0}
                      </StatNumber>
                    </Stat>
                  </Box>
                  <Box p={3} bg={headerBg} borderRadius="md">
                    <Stat>
                      <StatLabel>Portfolio</StatLabel>
                      <StatNumber fontSize="sm">
                        {selectedProgram.portfolioId?.portfolioName || "-"}
                      </StatNumber>
                    </Stat>
                  </Box>
                </SimpleGrid>
                {selectedProgram.projects?.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>
                      Projects
                    </Text>
                    <Table variant="simple" size="sm">
                      <Thead bg={headerBg}>
                        <Tr>
                          <Th>Project</Th>
                          <Th>Status</Th>
                          <Th>Type</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {selectedProgram.projects.map((proj) => (
                          <Tr key={proj._id}>
                            <Td>
                              <Text fontSize="sm">{proj.projectName}</Text>
                            </Td>
                            <Td>
                              <Badge colorScheme="green">{proj.status}</Badge>
                            </Td>
                            <Td>
                              <Text fontSize="sm">{proj.projectType}</Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDetailClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
